import React, { type ChangeEvent, cloneElement, isValidElement, useEffect, useState } from 'react'
import { ErrorIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import { Text } from './Text'

export type FormElementVariant = 'default' | 'error' | 'filled'

export interface FormElementStyleProps {
	variant?: FormElementVariant
	disabled?: boolean
	isFocused?: boolean
	hasValue?: boolean
}

export interface BaseFormElementProps extends FormElementStyleProps {
	label?: string
	hint?: React.ReactNode
	showHintIcon?: boolean
	isRequired?: boolean
	className?: string
}

export const formStyles = {
	container: cva('flex flex-col items-start gap-1 w-full'),

	element: cva('w-full px-4 py-3 text-base rounded-sm border border-neutral-300 transition-colors focus:outline-none', {
		variants: {
			variant: {
				default: 'text-neutral-500',
				error: 'border-[2px] border-error-600 focus:border-error-600',
				filled: 'bg-neutral-50 text-neutral-800',
			},
			isFocused: {
				true: 'border-[2.4px] -m-[1.2px] border-primary-400',
				false: '',
			},
			disabled: {
				true: 'text-neutral-300 bg-neutral-50 cursor-not-allowed',
				false: '',
			},
			hasValue: {
				true: 'text-neutral-800',
				false: '',
			},
			readOnly: {
				true: 'border-0 px-0 py-0',
				false: '',
			},
		},
		compoundVariants: [
			{
				variant: 'error',
				hasValue: false,
				className: 'text-neutral-500',
			},
			{
				variant: 'error',
				hasValue: true,
				className: 'text-neutral-800',
			},
			{
				variant: 'error',
				isFocused: true,
				className: 'border-[2px] border-error-600',
			},
		],
		defaultVariants: {
			variant: 'default',
			isFocused: false,
			disabled: false,
			hasValue: false,
		},
	}),

	label: cva('text-sm text-neutral-800 mb-1', {
		variants: {
			variant: {
				default: '',
				error: 'text-error-600',
				filled: '',
			},
			disabled: {
				true: 'text-neutral-400',
				false: '',
			},
		},
		defaultVariants: {
			variant: 'default',
			disabled: false,
		},
	}),

	hint: cva('flex items-center gap-1 text-neutral-800 mt-1', {
		variants: {
			variant: {
				default: '',
				error: 'text-error-600',
				filled: '',
			},
			disabled: {
				true: 'text-neutral-400',
				false: '',
			},
		},
		defaultVariants: {
			variant: 'default',
			disabled: false,
		},
	}),

	icon: cva('fill-neutral-800', {
		variants: {
			variant: {
				default: 'fill-neutral-500',
				error: 'fill-error-600',
				filled: 'fill-neutral-800',
			},
			disabled: {
				true: 'fill-neutral-300',
				false: '',
			},
			hasValue: {
				true: 'fill-neutral-800',
				false: '',
			},
			isOpen: {
				true: 'transform rotate-180',
				false: '',
			},
		},
		compoundVariants: [
			{
				variant: 'error',
				className: 'fill-error-600',
			},
			{
				variant: 'error',
				disabled: true,
				className: 'fill-neutral-300',
			},
		],
		defaultVariants: {
			variant: 'default',
			disabled: false,
			hasValue: false,
			isOpen: false,
		},
	}),
}

type ElementWithValue = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

export function useFormElementState<T extends ElementWithValue>(props: {
	value?: string | number | readonly string[] | undefined
	defaultValue?: string | number | readonly string[] | undefined
	variant?: FormElementVariant
	disabled?: boolean
	onChange?: (e: ChangeEvent<T>) => void
}) {
	const { value: controlledValue, defaultValue, variant, disabled, onChange } = props
	const [isFocused, setIsFocused] = useState(false)
	const [internalValue, setInternalValue] = useState<string>(
		controlledValue
			? String(controlledValue)
			: defaultValue !== undefined
				? Array.isArray(defaultValue)
					? defaultValue.join(',')
					: String(defaultValue)
				: '',
	)

	useEffect(() => {
		if (controlledValue !== undefined) {
			setInternalValue(String(controlledValue))
		}
	}, [controlledValue])

	const handleFocus = () => setIsFocused(true)
	const handleBlur = () => setIsFocused(false)

	const handleChange = (e: ChangeEvent<T>) => {
		const newValue = e.target.value
		setInternalValue(String(newValue))
		onChange?.(e)
	}

	const resolvedVariant: FormElementVariant = variant === 'error' ? 'error' : internalValue ? 'filled' : 'default'

	const styleProps: FormElementStyleProps = {
		variant: resolvedVariant,
		disabled,
		isFocused,
		hasValue: !!internalValue,
	}

	return {
		value: controlledValue ?? internalValue,
		handleChange,
		handleFocus,
		handleBlur,
		styleProps,
		resolvedVariant,
		internalValue,
		setInternalValue,
	}
}

export interface FormElementWrapperProps {
	label?: string
	hint?: React.ReactNode
	showHintIcon?: boolean
	variant?: FormElementVariant
	disabled?: boolean
	isRequired?: boolean
	children: React.ReactNode
	styleProps?: FormElementStyleProps
	className?: string
}
interface ChildWithStyleProps {
	styleProps?: FormElementStyleProps
}

export const FormElementWrapper: React.FC<FormElementWrapperProps> = ({
	label,
	hint,
	showHintIcon = false,
	variant = 'default',
	disabled = false,
	isRequired = false,
	children,
	styleProps,
	className,
}) => {
	const mergedStyleProps: FormElementStyleProps = styleProps || {
		variant,
		disabled,
	}

	const enhancedChildren = React.Children.map(children, (child) => {
		if (isValidElement(child)) {
			if (typeof child.type !== 'string') {
				const props = child.props as Record<string, unknown>
				if (!('styleProps' in props)) {
					return cloneElement(child, {
						styleProps: mergedStyleProps,
					} as React.JSX.IntrinsicAttributes & ChildWithStyleProps)
				}
			}
		}
		return child
	})

	return (
		<div className={cn(formStyles.container(), className)}>
			{label && (
				<label htmlFor={label} className={formStyles.label(mergedStyleProps)}>
					{label}
					{isRequired && (
						<span className='text-error-600 ml-0.5' aria-hidden='true'>
							*
						</span>
					)}
				</label>
			)}

			{enhancedChildren}

			{hint && (
				<div className={formStyles.hint(mergedStyleProps)}>
					{(showHintIcon || mergedStyleProps.variant === 'error') && (
						<ErrorIcon size={16} className={formStyles.icon(mergedStyleProps)} />
					)}
					<Text variant='hint'>{hint}</Text>
				</div>
			)}
		</div>
	)
}
